import { Component, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ChartType } from '@core/enums/chart-type';
import { StateDashboardTiles } from '@core/enums/state-dashboard';
import { ILoginResponse } from '@core/interfaces/ilogin-response';
import { IStateDashboardTileResponse, IStateDashboardConfiguration, IStateDashboardResponse, IChildrenInSpedResponse } from '@core/interfaces/istate-dashboard';
import { AuthService } from '@core/services/auth.service';
import { getIepIfspColorClasses } from '@core/services/helper.service';
import { StateService } from '@core/services/state.service';
import { take, tap } from 'rxjs';
import { UserSessionComponent } from '../dashboard/user-session/user-session.component';

@Component({
    selector: 'drdp-state-dashboard',
    templateUrl: './state-dashboard.component.html',
    styleUrls: ['./state-dashboard.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class StateDashboardComponent implements OnInit {
  private stateService = inject(StateService);
  private authService = inject(AuthService);
  private router = inject(Router);  
  public modal = inject(MatDialog);

  user: ILoginResponse = this.authService.getCurrentUser();
  tiles = StateDashboardTiles;
  genderDemographics: IStateDashboardTileResponse[] = [];
  childEnrollments: IStateDashboardTileResponse[] = [];
  ageCounts: IStateDashboardTileResponse[] = [];
  raceCounts: IStateDashboardTileResponse[] = [];
  ageGroupCounts: IStateDashboardTileResponse[] = [];
  programAgeGroupCounts: IStateDashboardTileResponse[] = [];
  homeLanguageTotal: IStateDashboardTileResponse[] = [];
  homeLanguages: IStateDashboardTileResponse[] = [];
  childrenInSped: IChildrenInSpedResponse | null = null;
  ratingsCounts: IStateDashboardTileResponse[] = [];
  iepIfspGroups: IStateDashboardTileResponse[] = [];
  disabilityCounts: IStateDashboardTileResponse[] = [];
  chartType = ChartType;
  isViewLanguage: boolean = false;
  dashboardResponse: IStateDashboardResponse | null = null;
  getIepIfspColorClasses = getIepIfspColorClasses;
  activeTiles = {
    genderDemographic: false,
    enrollment: false,
    ageBasedOnDob: false,
    childByRace: false,
    ageGroup: false,
    programByAgeGroup: false,
    homeLanguage: false,
    childInSped: false,
    rating: false,
    childByIep: false,
    disability: false
  };

  ngOnInit(): void {
    if (this.user.sessionCount > 1) this.openSessionModel();

    this.stateService.getStateDashboardData(this.user.stateDashboardPermissionId).pipe(
      take(1), 
      tap((response: IStateDashboardResponse) => {
        if (!response.dashboardEnabled) {
          this.router.navigate(['dashboard']);
        } else {
          this.dashboardResponse = response;
          if (response.configurations) this.getActiveTiles(response.configurations);
        }
      })
    ).subscribe(() => {
      this.initializeCharts();
    });
  }

  openSessionModel(): void {
    const modalRef = this.modal.open(UserSessionComponent);
    modalRef.afterClosed().subscribe();
  }

  getActiveTiles(controls: IStateDashboardConfiguration[]): void {
    controls.forEach((control: IStateDashboardConfiguration) => {
      if (control.controlName in this.activeTiles) {
        const key = control.controlName as keyof typeof this.activeTiles;
        this.activeTiles[key] = control.isActive;
      }
    });
  }

  initializeCharts(): void {
    if (this.activeTiles.genderDemographic) {
      this.stateService.getGenderDemographicsCounts()
      .pipe(take(1)).subscribe((response: IStateDashboardTileResponse[]) => {
        this.genderDemographics = response;
      });
    }

    if (this.activeTiles.enrollment) {
      this.stateService.getChildEnrollmentsCounts()
      .pipe(take(1)).subscribe((response: IStateDashboardTileResponse[]) => {
        this.childEnrollments = response;
      });
    }

    if (this.activeTiles.ageBasedOnDob) {
      this.stateService.getChildAgeCounts()
      .pipe(take(1)).subscribe((response: IStateDashboardTileResponse[]) => {
        this.ageCounts = response;
      });
    }

    if (this.activeTiles.childByRace) {
      this.stateService.getChildRaceCounts()
      .pipe(take(1)).subscribe((response: IStateDashboardTileResponse[]) => {
        this.raceCounts = response;
      });
    }

    if (this.activeTiles.ageGroup) {
      this.stateService.getChildAgeGroupCounts()
      .pipe(take(1)).subscribe((response: IStateDashboardTileResponse[]) => {
        this.ageGroupCounts = response;
      });
    }

    if (this.activeTiles.programByAgeGroup) {
      this.stateService.getProgramsByAgeGroupCounts()
      .pipe(take(1)).subscribe((response: IStateDashboardTileResponse[]) => {
        this.programAgeGroupCounts = response;
      });
    }

    if (this.activeTiles.homeLanguage) {
      this.stateService.getStudentCountsByLangTypes()
      .pipe(take(1)).subscribe((response: IStateDashboardTileResponse[]) => {
        this.homeLanguageTotal = response;
      });
    }

    if (this.activeTiles.childInSped) {
      this.stateService.getChildrenInSpedCounts()
      .pipe(take(1)).subscribe((response: IChildrenInSpedResponse) => {
        this.childrenInSped = response;
      });
    }

    if (this.activeTiles.rating) {
      this.stateService.getRatingsCounts()
      .pipe(take(1)).subscribe((response: IStateDashboardTileResponse[]) => {
        this.ratingsCounts = response;
      });
    }

    if (this.activeTiles.childByIep) {
      this.stateService.getChildrenWithIepCounts()
      .pipe(take(1)).subscribe((response: IStateDashboardTileResponse[]) => {
        this.groupChildrenWithIepIfsp(response);
      });
    }

    if (this.activeTiles.disability) {
      this.stateService.getDisabilityChildCounts()
      .pipe(take(1)).subscribe((response: IStateDashboardTileResponse[]) => {
        this.disabilityCounts = response;
      });
    }
  }

  private groupChildrenWithIepIfsp(response: IStateDashboardTileResponse[]): void {
    const groups = new Map<string, IStateDashboardTileResponse>();

    for (const item of response) {
      let group = groups.get(item.name);
      if (!group) {
        group = { name: item.name, count: 0, total: 0, color: item.color, items: [] };
        groups.set(item.name, group);
      }

      if (item.subName) {
        group.items!.push(item);
      } else {
        group.count = item.count;
      }
    }

    this.iepIfspGroups = Array.from(groups.values());
  }

  onViewLanguageClick(): void {
    this.isViewLanguage = !this.isViewLanguage;
    if (!this.homeLanguages.length) {
      this.stateService.getHomeLanguageCounts()
      .pipe(take(1)).subscribe((response: IStateDashboardTileResponse[]) => {
        this.homeLanguages = response;
      });
    }
  }
}
